import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

type PlanType = 'basic' | 'standard' | 'advance' | 'none';
type Feature =
  | 'advanceFilter'
  | 'basicFilter'
  | 'search'
  | 'downloadCSV'
  | 'allIndiaAccess'
  | 'pdpTenderOverview'
  | 'pdpTenderDescription'
  | 'unlimitedPDPView';

@Injectable({
  providedIn: 'root'
})
export class PlanAccessService {
  public userPlan: PlanType = 'none'; // default
  private destroy$ = new Subject<void>();

  private userPlanSubject = new BehaviorSubject<PlanType>('none');
  userPlan$ = this.userPlanSubject.asObservable();

  private featureMatrix: Record<PlanType, Record<Feature, boolean>> = {
    advance: {
      advanceFilter: true,
      basicFilter: true,
      search: true,
      downloadCSV: true,
      allIndiaAccess: true,
      pdpTenderOverview: true,
      pdpTenderDescription: true,
      unlimitedPDPView: true,
    },
    standard: {
      advanceFilter: false,
      basicFilter: true,
      search: true,
      downloadCSV: false,
      allIndiaAccess: true,
      pdpTenderOverview: true,
      pdpTenderDescription: true,
      unlimitedPDPView: true,
    },
    basic: {
      advanceFilter: false,
      basicFilter: true,
      search: true,
      downloadCSV: false,
      allIndiaAccess: false,
      pdpTenderOverview: false,
      pdpTenderDescription: false,
      unlimitedPDPView: false,
    },
    none: {
      advanceFilter: false,
      basicFilter: false,
      search: false,
      downloadCSV: false,
      allIndiaAccess: false,
      pdpTenderOverview: false,
      pdpTenderDescription: false,
      unlimitedPDPView: false,
    },
  };

  constructor(private authService: AuthService) {
    this.authService.user$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateUserPlan();
      });

    // Always try to update (even for guests)
    this.updateUserPlan();
  }

  private async updateUserPlan(): Promise<void> {
    const { plan } = await this.authService.getPlanAndDaysLeft();
    const normalizedPlan = plan?.toLowerCase?.() || 'none';
    const typedPlan = (['basic', 'standard', 'advance'].includes(normalizedPlan) ? normalizedPlan : 'none') as PlanType;
  
    if (this.userPlan !== typedPlan) {
      this.userPlan = typedPlan;
      this.userPlanSubject.next(typedPlan);
    }
  }

  hasAccess(feature: Feature): boolean {
    return this.featureMatrix[this.userPlan]?.[feature] || false;
  }

  hasAccessToState(stateSlug: string): boolean {
    if (this.userPlan !== 'basic') return true;
    const allowedStates = this.authService.getUserAllowedStates();
    return allowedStates.includes(stateSlug);
  }

  getUserPackage(): PlanType {
    return this.userPlan;
  }

  destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}